import { useCallback, useState } from 'react';

export interface SelectionApi<ID extends string | number> {
  selectedIds: Set<ID>;
  isSelected: (id: ID) => boolean;
  toggle: (id: ID) => void;
  selectMany: (ids: ID[]) => void;
  clear: () => void;
  allSelected: (ids: ID[]) => boolean;
  count: number;
}

export function useSelection<ID extends string | number>(): SelectionApi<ID> {
  const [selectedIds, setSelected] = useState<Set<ID>>(() => new Set());

  const isSelected = useCallback((id: ID) => selectedIds.has(id), [selectedIds]);

  const toggle = useCallback((id: ID) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectMany = useCallback((ids: ID[]) => {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const allSelected = useCallback((ids: ID[]) => ids.every(id => selectedIds.has(id)), [selectedIds]);

  return {
    selectedIds,
    isSelected,
    toggle,
    selectMany,
    clear,
    allSelected,
    count: selectedIds.size,
  };
}
